import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Browser-side Supabase client. Uses the **publishable** key (safe to
 * ship in the bundle, designed to be public). Used for Realtime
 * subscriptions AND, post-v1.0 Slice 1, for user authentication —
 * mutations to game state still go through Vercel Functions, but
 * the Functions now identify callers by the session JWT this client
 * mints.
 *
 * Lazy + cached: only constructed on first call, reused thereafter.
 * Single-player builds (no /game/:id URL) still instantiate it once
 * the auth foundation lands so the Sign In button on HomeScene can
 * surface a session even before the user touches MP.
 */
let cached: SupabaseClient | null = null;

export function supabaseBrowser(): SupabaseClient {
  if (cached) return cached;

  const url = import.meta.env['VITE_SUPABASE_URL'];
  const key = import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY'];
  if (!url || !key) {
    throw new Error(
      'Missing VITE_SUPABASE_URL / VITE_SUPABASE_PUBLISHABLE_KEY env vars'
    );
  }

  cached = createClient(url, key, {
    // persistSession + autoRefreshToken both ON now that we have
    // real users. Without persistSession, every page refresh would
    // log the user out — terrible UX for a game where you reload
    // between rounds. autoRefreshToken keeps the JWT valid across
    // long async-turn sessions (matters once v1.0 timer-based MP
    // games last for days).
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true, // for password-reset / email-confirm redirects
    },
    realtime: { params: { eventsPerSecond: 10 } },
  });
  return cached;
}
